import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Facebook,
  Instagram,
  MessageCircle,
  Music2,
  ShoppingBag,
  Minus,
  Plus,
  Trash2,
  X,
} from "lucide-react";

const formatMoney = (value) => {
  const amount = Number(value) || 0;
  return `${new Intl.NumberFormat("fr-FR").format(amount)} F`;
};

const buildVariationLabel = (variation) => {
  if (!variation) return "";
  const attrs = variation.attributes || variation.variation_attributes || {};
  const pairs = Object.entries(attrs)
    .map(([key, val]) => `${String(key).trim()} : ${String(val).trim()}`)
    .filter(Boolean);
  if (pairs.length > 0) return pairs.join(" · ");
  return variation.name || variation.variation_name || `Variation ${variation.id}`;
};

const getProductImageSrc = (product) =>
  product?.image_url || product?.image_path || "/placeholder-menu.jpg";

const sanitizePhoneForWaMe = (value) => {
  const digits = String(value || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  return digits;
};

const splitExtras = (extras) => {
  const list = Array.isArray(extras) ? extras : [];
  const accompaniments = [];
  const supplements = [];
  list.forEach((extra) => {
    const type = extra?.type;
    if (type === "accompaniment") accompaniments.push(extra);
    else if (type === "supplement") supplements.push(extra);
  });
  return { accompaniments, supplements };
};

const MENU_CART_STORAGE_KEY = "moo_menu_public_cart_v1";
const MENU_ORDER_STORAGE_KEY = "moo_menu_public_order_v1";
const MENU_SESSION_STORAGE_KEY = "moo_menu_public_session_v1";
const MENU_TABLE_LABEL_STORAGE_KEY = "moo_menu_public_table_label_v1";

const getScopedMenuStorageKey = (baseKey, subdomain = "") => {
  const scope = String(subdomain || "").trim().toLowerCase();
  return scope ? `${baseKey}:${scope}` : baseKey;
};

const resolveProductEffectivePrice = (product) =>
  Number(product?.effective_price_for_day ?? product?.display_price ?? product?.price ?? 0) || 0;

const resolveVariationEffectivePrice = (variation) =>
  Number(variation?.effective_price_for_day ?? variation?.daily_menu_price ?? variation?.price ?? 0) || 0;

const getHostSubdomain = () => {
  try {
    const host = window.location.hostname || "";
    const parts = host.split(".").filter(Boolean);
    if (parts.length < 3) return "";
    return (parts[0] || "").toLowerCase();
  } catch (_e) {
    return "";
  }
};

const getQuerySubdomain = () => {
  try {
    const params = new URLSearchParams(window.location.search || "");
    return String(params.get("subdomain") || "")
      .trim()
      .toLowerCase();
  } catch (_e) {
    return "";
  }
};

export default function MenuPage() {
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState("daily");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [extrasByProductId, setExtrasByProductId] = useState(new Map());
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [coverIndex, setCoverIndex] = useState(0);
  const [descIndex, setDescIndex] = useState(0);
  const [heroScale] = useState(1.05);
  const [heroFade, setHeroFade] = useState(1);
  const [heroDim, setHeroDim] = useState(0);
  const [packageMenuEnabled, setPackageMenuEnabled] = useState(null);
  const [packageMenuMode, setPackageMenuMode] = useState("catalog");
  const [cartItems, setCartItems] = useState([]);
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [menuTableLabel, setMenuTableLabel] = useState("");
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [activeTracking, setActiveTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingCodeInput, setTrackingCodeInput] = useState("");
  const [selectionQuantity, setSelectionQuantity] = useState(1);
  const [selectedVariationId, setSelectedVariationId] = useState(null);
  const [selectedExtraQuantities, setSelectedExtraQuantities] = useState({});
  const [selectionError, setSelectionError] = useState("");

  const [headerPinned, setHeaderPinned] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  const heroRef = useRef(null);
  const headerRef = useRef(null);
  const headerSentinelRef = useRef(null);
  const headerOffsetRef = useRef(Number.POSITIVE_INFINITY);
  const categoriesRef = useRef(null);
  const sectionsRef = useRef(new Map());
  const observerRef = useRef(null);
  const activeTrackingPollRef = useRef(null);
  const tenantSubdomain = useMemo(() => getQuerySubdomain() || getHostSubdomain(), []);
  const clientSessionIdRef = useRef("");
  const scopedStorageKeys = useMemo(
    () => ({
      cart: getScopedMenuStorageKey(MENU_CART_STORAGE_KEY, tenantSubdomain),
      order: getScopedMenuStorageKey(MENU_ORDER_STORAGE_KEY, tenantSubdomain),
      session: getScopedMenuStorageKey(MENU_SESSION_STORAGE_KEY, tenantSubdomain),
      table: getScopedMenuStorageKey(MENU_TABLE_LABEL_STORAGE_KEY, tenantSubdomain),
    }),
    [tenantSubdomain],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const subdomainQuery = tenantSubdomain
          ? `?subdomain=${encodeURIComponent(tenantSubdomain)}`
          : "";
        const [restaurantRes, packagesRes, categoriesRes, productsRes, menuConfigRes] = await Promise.all([
          fetch("/api/restaurant-info").catch(() => null),
          fetch("/api/packages").catch(() => null),
          fetch("/api/categories"),
          fetch("/api/products"),
          fetch(`/api/cloud/public/menu/config${subdomainQuery}`).catch(() => null),
        ]);

        if (cancelled) return;

        if (restaurantRes?.ok) {
          const info = await restaurantRes.json();
          if (!cancelled) setRestaurantInfo(info || null);
        }
        if (packagesRes?.ok) {
          const data = await packagesRes.json();
          const list = Array.isArray(data?.packages) ? data.packages : [];
          const activeId = data?.activeId || list[0]?.id || "";
          const activePkg =
            list.find((pkg) => String(pkg?.id) === String(activeId)) || list[0] || null;
          const menuFlag =
            typeof activePkg?.menu_enabled === "boolean"
              ? activePkg.menu_enabled
              : typeof activePkg?.menuEnabled === "boolean"
                ? activePkg.menuEnabled
                : null;
          const menuModeRaw = String(
            activePkg?.menu_mode ?? activePkg?.menuMode ?? "catalog",
          ).toLowerCase();
          const resolvedMode = menuModeRaw === "ordering" ? "ordering" : "catalog";
          if (!cancelled) setPackageMenuEnabled(menuFlag);
          if (!cancelled) setPackageMenuMode(resolvedMode);
        }

        if (menuConfigRes?.ok) {
          const menuConfig = await menuConfigRes.json();
          if (!cancelled && typeof menuConfig?.menu_enabled === "boolean") {
            setPackageMenuEnabled(menuConfig.menu_enabled);
          }
          if (!cancelled) {
            const modeRaw = String(menuConfig?.menu_mode || "catalog").toLowerCase();
            setPackageMenuMode(modeRaw === "ordering" ? "ordering" : "catalog");
          }
        }

        const categoriesData = categoriesRes.ok ? await categoriesRes.json() : [];
        const productsData = productsRes.ok ? await productsRes.json() : [];

        if (cancelled) return;

        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setProducts(
          (Array.isArray(productsData) ? productsData : []).filter((p) => {
            if (!p) return false;
            if (p.is_active === 0) return false;
            if (p.is_available === 0) return false;
            return true;
          }),
        );
      } catch (e) {
        console.error("MenuPage load error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tenantSubdomain]);

  useEffect(() => {
    try {
      const name = String(restaurantInfo?.name || restaurantInfo?.restaurant_name || "").trim();
      if (name) {
        document.title = `${name} · Menu`;
      }
      const logo = String(restaurantInfo?.logo_path || "").trim();
      if (!logo || !logo.startsWith("/")) return;
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = logo;
    } catch (_e) {
      // ignore
    }
  }, [restaurantInfo]);

  useEffect(() => {
    const id = "moo-menu-hero-zoom";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.innerHTML = `
        @keyframes mooHeroZoom {
          0% { transform: scale(1); }
          50% { transform: scale(1.06); }
          100% { transform: scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    let raf = null;
    const update = () => {
      raf = null;
      const heroEl = heroRef.current;
      const heroHeight = heroEl?.offsetHeight || window.innerHeight || 1;
      const scrollY = window.scrollY || 0;
      const progress = Math.min(1, Math.max(0, scrollY / heroHeight));
      setHeroFade(1 - Math.min(1, progress * 1.15));
      setHeroDim(Math.min(1, progress * 1.1));
    };
    const onScroll = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== null) window.cancelAnimationFrame(raf);
    };
  }, []);


  useEffect(() => {
    const onWheel = (event) => {
      const rail = categoriesRef.current;
      if (!rail) return;
      const rect = rail.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;
      rail.scrollLeft += event.deltaY * 0.35;
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    const migrateLegacyKey = (legacyKey, scopedKey) => {
      try {
        const scopedValue = localStorage.getItem(scopedKey);
        if (scopedValue !== null) return;
        const legacyValue = localStorage.getItem(legacyKey);
        if (legacyValue !== null) {
          localStorage.setItem(scopedKey, legacyValue);
        }
      } catch (_e) {
        // ignore
      }
    };

    migrateLegacyKey(MENU_SESSION_STORAGE_KEY, scopedStorageKeys.session);
    migrateLegacyKey(MENU_CART_STORAGE_KEY, scopedStorageKeys.cart);
    migrateLegacyKey(MENU_ORDER_STORAGE_KEY, scopedStorageKeys.order);
    migrateLegacyKey(MENU_TABLE_LABEL_STORAGE_KEY, scopedStorageKeys.table);

    try {
      const existingSession = localStorage.getItem(scopedStorageKeys.session);
      if (existingSession) {
        clientSessionIdRef.current = existingSession;
      } else {
        const generated = `menu_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        clientSessionIdRef.current = generated;
        localStorage.setItem(scopedStorageKeys.session, generated);
      }
    } catch (_e) {
      clientSessionIdRef.current = `menu_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }

    try {
      const rawCart = localStorage.getItem(scopedStorageKeys.cart);
      if (rawCart) {
        const parsedCart = JSON.parse(rawCart);
        if (Array.isArray(parsedCart)) {
          setCartItems(parsedCart);
        }
      }
    } catch (_e) {
      // ignore
    }

    try {
      const rawOrder = localStorage.getItem(scopedStorageKeys.order);
      if (rawOrder) {
        const parsedOrder = JSON.parse(rawOrder);
        if (parsedOrder && parsedOrder.tracking_code) {
          setActiveTracking(parsedOrder);
          if (parsedOrder.table_label) {
            setMenuTableLabel(String(parsedOrder.table_label));
          }
        }
      }
    } catch (_e) {
      // ignore
    }

    try {
      const storedTableLabel = localStorage.getItem(scopedStorageKeys.table);
      if (storedTableLabel) {
        setMenuTableLabel(String(storedTableLabel));
      }
    } catch (_e) {
      // ignore
    }
  }, [scopedStorageKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(scopedStorageKeys.cart, JSON.stringify(cartItems || []));
    } catch (_e) {
      // ignore
    }
  }, [cartItems, scopedStorageKeys]);

  useEffect(() => {
    try {
      if (!activeTracking) {
        localStorage.removeItem(scopedStorageKeys.order);
      } else {
        localStorage.setItem(
          scopedStorageKeys.order,
          JSON.stringify({
            tracking_code: activeTracking.tracking_code,
            public_order_id: activeTracking.public_order_id,
            status: activeTracking.status,
            raw_status: activeTracking.raw_status,
            table_label: activeTracking.table_label || menuTableLabel || "",
          }),
        );
      }
    } catch (_e) {
      // ignore
    }
  }, [activeTracking, menuTableLabel, scopedStorageKeys]);

  useEffect(() => {
    try {
      const value = String(menuTableLabel || "").trim();
      if (!value) {
        localStorage.removeItem(scopedStorageKeys.table);
      } else {
        localStorage.setItem(scopedStorageKeys.table, value);
      }
    } catch (_e) {
      // ignore
    }
  }, [menuTableLabel, scopedStorageKeys]);

  useEffect(() => {
    const headerEl = headerRef.current;
    const sentinelEl = headerSentinelRef.current;
    if (!headerEl || !sentinelEl) return undefined;

    let raf = null;
    const updateMetrics = () => {
      const sentinelRect = sentinelEl.getBoundingClientRect();
      headerOffsetRef.current = sentinelRect.top + window.scrollY;

      const headerRect = headerEl.getBoundingClientRect();
      setHeaderHeight(headerRect.height || 0);

      const shouldPin = window.scrollY >= headerOffsetRef.current;
      setHeaderPinned((prev) => (prev === shouldPin ? prev : shouldPin));
    };

    const schedule = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        updateMetrics();
      });
    };

    updateMetrics();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(headerEl);
    }

    window.addEventListener("resize", schedule);
    window.addEventListener("load", schedule);

    return () => {
      if (raf !== null) window.cancelAnimationFrame(raf);
      resizeObserver?.disconnect?.();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("load", schedule);
    };
  }, []);

  useEffect(() => {
    let raf = null;
    const update = () => {
      raf = null;
      const shouldPin = window.scrollY >= headerOffsetRef.current;
      setHeaderPinned((prev) => (prev === shouldPin ? prev : shouldPin));
    };
    const onScroll = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf !== null) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    const rail = categoriesRef.current;
    if (!rail) return;
    const btn = rail.querySelector(`[data-cat="${activeKey}"]`);
    if (!btn) return;
    const offset = btn.offsetLeft;
    rail.scrollTo({ left: Math.max(0, offset - 12), behavior: "smooth" });
  }, [activeKey]);

  const dailyMenuProducts = useMemo(() => {
    return (products || []).filter((product) => {
      if (product?.daily_menu_active_today) return true;
      const variations = Array.isArray(product?.variations) ? product.variations : [];
      return variations.some((v) => !!v?.daily_menu_active_today);
    });
  }, [products]);

  const categoriesWithDaily = useMemo(() => {
    const base = Array.isArray(categories) ? categories : [];
    if (dailyMenuProducts.length === 0) return base;
    return [
      {
        id: "daily",
        name: "Menu du jour",
        products_count: dailyMenuProducts.length,
      },
      ...base,
    ];
  }, [categories, dailyMenuProducts.length]);

  const productsByCategoryKey = useMemo(() => {
    const map = new Map();

    // Daily menu
    if (dailyMenuProducts.length > 0) {
      map.set("daily", dailyMenuProducts);
    }

    products.forEach((product) => {
      const key = product.category_id ?? product.categoryId ?? null;
      if (key === null || key === undefined) return;
      if (!map.has(String(key))) map.set(String(key), []);
      map.get(String(key)).push(product);
    });

    // Stable ordering
    for (const [key, list] of map.entries()) {
      map.set(
        key,
        list.slice().sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""))),
      );
    }

    return map;
  }, [dailyMenuProducts, products]);

  const visibleCategoryKeys = useMemo(() => {
    return categoriesWithDaily
      .map((c) => String(c.id))
      .filter((key) => (productsByCategoryKey.get(key) || []).length > 0);
  }, [categoriesWithDaily, productsByCategoryKey]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const sectionEls = visibleCategoryKeys
      .map((key) => sectionsRef.current.get(key))
      .filter(Boolean);

    if (sectionEls.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.dataset?.key) setActiveKey(visible.target.dataset.key);
      },
      { root: null, threshold: [0.2, 0.35, 0.5, 0.75] },
    );

    sectionEls.forEach((el) => observerRef.current.observe(el));

    return () => {
      observerRef.current?.disconnect?.();
      observerRef.current = null;
    };
  }, [visibleCategoryKeys]);

  const scrollToCategory = (key) => {
    const section = sectionsRef.current.get(String(key));
    if (!section) return;
    setActiveKey(String(key));
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ensureExtrasLoaded = async (productId) => {
    if (!productId) return;
    if (extrasByProductId.has(productId)) return;
    setExtrasLoading(true);
    try {
      const res = await fetch(`/api/products/${productId}/extras`);
      const data = res.ok ? await res.json() : [];
      setExtrasByProductId((prev) => {
        const next = new Map(prev);
        next.set(productId, Array.isArray(data) ? data : []);
        return next;
      });
    } catch (e) {
      console.error("MenuPage extras error:", e);
    } finally {
      setExtrasLoading(false);
    }
  };

  const openProduct = (product) => {
    setSelectedProduct(product);
    setSelectionQuantity(1);
    setSelectionError("");
    const variations = Array.isArray(product?.variations) ? product.variations : [];
    setSelectedVariationId(
      product?.product_type === "variable" && variations.length > 0
        ? Number(variations[0]?.id) || null
        : null,
    );
    setSelectedExtraQuantities({});
    if (product?.id != null) {
      ensureExtrasLoaded(product.id);
    }
  };

  useEffect(() => {
    if (!selectedProduct?.id) return;
    const extras = extrasByProductId.get(selectedProduct.id) || [];
    if (!extras.length) return;
    setSelectedExtraQuantities((prev) => {
      const next = { ...prev };
      let changed = false;
      extras.forEach((extra) => {
        const extraId = Number(extra?.id);
        if (!Number.isFinite(extraId)) return;
        if (next[extraId] !== undefined) return;
        if (extra?.is_default || extra?.isDefault) {
          next[extraId] = 1;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedProduct, extrasByProductId]);

  const closeProduct = () => {
    setSelectionError("");
    setSelectedProduct(null);
  };

  const renderPrice = (product) => {
    const isVariable = product?.product_type === "variable";
    const min = Number(product?.price_min_today ?? product?.price_min ?? product?.price ?? 0) || 0;
    const max = Number(product?.price_max_today ?? product?.price_max ?? product?.price ?? 0) || 0;
    const effective = Number(product?.effective_price_for_day ?? product?.display_price ?? product?.price ?? 0) || 0;
    const base = Number(product?.price ?? 0) || 0;
    const isPromo = !!product?.daily_menu_active_today && effective > 0 && base > 0 && effective !== base;

    if (!isVariable) {
      return (
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-white">{formatMoney(effective)}</span>
          {isPromo && (
            <span className="text-sm text-slate-400 line-through">{formatMoney(base)}</span>
          )}
        </div>
      );
    }

    const display =
      min === max ? formatMoney(min) : `${formatMoney(min)} – ${formatMoney(max)}`;
    return <div className="text-lg font-semibold text-white">{display}</div>;
  };

  const title = restaurantInfo?.name || restaurantInfo?.restaurant_name || "Menu";
  const logoPath = restaurantInfo?.logo_path || "";
  const menuDisabled =
    packageMenuEnabled === false ||
    (packageMenuEnabled == null && restaurantInfo?.menu_enabled === false);
  const menuMode = packageMenuMode === "ordering" ? "ordering" : "catalog";
  const isOrderingMode = !menuDisabled && menuMode === "ordering";
  const cartItemsCount = useMemo(
    () => (cartItems || []).reduce((sum, item) => sum + Number(item?.quantity || 0), 0),
    [cartItems],
  );
  const cartTotal = useMemo(
    () =>
      (cartItems || []).reduce(
        (sum, item) =>
          sum + (Number(item?.unit_total_price || 0) * Number(item?.quantity || 0) || 0),
        0,
      ),
    [cartItems],
  );
  const canSubmitOrder =
    isOrderingMode &&
    !submittingOrder &&
    cartItems.length > 0 &&
    String(menuTableLabel || "").trim().length > 0;

  const selectedProductExtras = useMemo(() => {
    if (!selectedProduct?.id) return [];
    return extrasByProductId.get(selectedProduct.id) || [];
  }, [selectedProduct, extrasByProductId]);

  const selectedVariation = useMemo(() => {
    if (!selectedProduct || selectedProduct.product_type !== "variable") return null;
    const variations = Array.isArray(selectedProduct.variations) ? selectedProduct.variations : [];
    if (!variations.length) return null;
    const wantedId = Number(selectedVariationId);
    return (
      variations.find((variation) => Number(variation?.id) === wantedId) ||
      variations[0] ||
      null
    );
  }, [selectedProduct, selectedVariationId]);

  const selectedExtrasList = useMemo(() => {
    if (!selectedProduct?.id) return [];
    const extras = selectedProductExtras || [];
    return extras
      .map((extra) => {
        const qty = Number(selectedExtraQuantities?.[Number(extra?.id)] || 0);
        if (!Number.isFinite(qty) || qty <= 0) return null;
        const type = String(extra?.type || "").toLowerCase() === "accompaniment"
          ? "accompaniment"
          : "supplement";
        const unitPrice =
          type === "accompaniment" ? 0 : Number(extra?.price ?? extra?.base_price ?? 0) || 0;
        return {
          id: Number(extra?.id),
          name: extra?.name || "Option",
          type,
          quantity: qty,
          price: unitPrice,
          max_quantity: Number(extra?.max_quantity ?? extra?.maxQuantity ?? 1) || 1,
        };
      })
      .filter(Boolean);
  }, [selectedExtraQuantities, selectedProduct, selectedProductExtras]);

  const selectedUnitBasePrice = useMemo(() => {
    if (!selectedProduct) return 0;
    if (selectedProduct.product_type === "variable") {
      return resolveVariationEffectivePrice(selectedVariation);
    }
    return resolveProductEffectivePrice(selectedProduct);
  }, [selectedProduct, selectedVariation]);

  const selectedUnitExtrasPrice = useMemo(
    () =>
      selectedExtrasList.reduce(
        (sum, extra) => sum + Number(extra?.price || 0) * Number(extra?.quantity || 1),
        0,
      ),
    [selectedExtrasList],
  );
  const selectedUnitTotalPrice = selectedUnitBasePrice + selectedUnitExtrasPrice;
  const selectedLineTotal = selectedUnitTotalPrice * Math.max(1, Number(selectionQuantity || 1));

  const statusClassMap = {
    amber: "border-amber-300/30 bg-amber-400/10 text-amber-100",
    blue: "border-sky-300/30 bg-sky-400/10 text-sky-100",
    emerald: "border-emerald-300/30 bg-emerald-400/10 text-emerald-100",
    rose: "border-rose-300/30 bg-rose-400/10 text-rose-100",
    slate: "border-slate-300/20 bg-slate-400/10 text-slate-100",
  };

  const computeLineSignature = (line) => {
    const extrasSig = (line?.extras || [])
      .map((extra) => `${extra.id}:${extra.quantity}`)
      .sort()
      .join("|");
    return `${line?.product_id || line?.productId || "0"}::${line?.variation_id || "base"}::${extrasSig}`;
  };

  const addLineToCart = (line) => {
    const signature = computeLineSignature(line);
    setCartItems((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex((item) => computeLineSignature(item) === signature);
      if (idx >= 0) {
        const nextQty = Number(list[idx].quantity || 0) + Number(line.quantity || 1);
        list[idx] = {
          ...list[idx],
          quantity: nextQty,
          total_price: Number(list[idx].unit_total_price || 0) * nextQty,
        };
      } else {
        list.push({
          ...line,
          total_price: Number(line.unit_total_price || 0) * Number(line.quantity || 1),
        });
      }
      return list;
    });
  };

  const adjustCartLineQty = (signature, delta) => {
    setCartItems((prev) => {
      const next = (Array.isArray(prev) ? prev : []).map((line) => ({ ...line }));
      const idx = next.findIndex((line) => computeLineSignature(line) === signature);
      if (idx < 0) return prev;
      const currentQty = Number(next[idx].quantity || 0);
      const nextQty = currentQty + delta;
      if (nextQty <= 0) {
        next.splice(idx, 1);
        return next;
      }
      next[idx].quantity = nextQty;
      next[idx].total_price = Number(next[idx].unit_total_price || 0) * nextQty;
      return next;
    });
  };

  const removeCartLine = (signature) => {
    setCartItems((prev) =>
      (Array.isArray(prev) ? prev : []).filter(
        (line) => computeLineSignature(line) !== signature,
      ),
    );
  };

  const handleAddSelectedProductToCart = () => {
    if (!selectedProduct) return;
    if (
      selectedProduct.product_type === "variable" &&
      (!selectedVariation || !Number(selectedVariation?.id))
    ) {
      setSelectionError("Choisis une variante pour continuer.");
      return;
    }
    const qty = Math.max(1, Number(selectionQuantity || 1));
    const line = {
      product_id: Number(selectedProduct.id),
      product_name: selectedProduct.name,
      image: getProductImageSrc(selectedProduct),
      quantity: qty,
      unit_price: selectedUnitBasePrice,
      unit_total_price: selectedUnitTotalPrice,
      variation_id: selectedVariation ? Number(selectedVariation.id) : null,
      variation_name: selectedVariation?.name || null,
      variation_attributes: selectedVariation?.attributes || selectedVariation?.variation_attributes || null,
      extras: selectedExtrasList,
    };
    addLineToCart(line);
    closeProduct();
    setShowCartDrawer(true);
  };

  const fetchTracking = async (trackingCode, silent = false) => {
    if (!trackingCode) return null;
    if (!silent) setTrackingLoading(true);
    try {
      const qs = tenantSubdomain ? `?subdomain=${encodeURIComponent(tenantSubdomain)}` : "";
      const response = await fetch(
        `/api/cloud/public/menu/orders/${encodeURIComponent(trackingCode)}${qs}`,
      );
      if (!response.ok) return null;
      const data = await response.json();
      const nextOrder = data?.order || null;
      if (nextOrder) {
        const resolvedTracking =
          data?.tracking_code ||
          nextOrder?.tracking_display_code ||
          trackingCode;
        setActiveTracking({
          tracking_code: resolvedTracking,
          tracking_internal_code: data?.tracking_internal_code || nextOrder?.tracking_code || null,
          tracking_day_key: data?.tracking_day_key || nextOrder?.tracking_day_key || null,
          public_order_id: nextOrder.public_order_id || null,
          status: data?.status || nextOrder.client_status || nextOrder.status_internal || "pending",
          raw_status: nextOrder.status_internal || "pending",
          table_label: nextOrder.table_label_client || menuTableLabel || "",
          order: nextOrder,
        });
        if (!silent) {
          setTrackingCodeInput(String(resolvedTracking).toUpperCase());
        }
      }
      return nextOrder;
    } catch (_e) {
      return null;
    } finally {
      if (!silent) setTrackingLoading(false);
    }
  };

  const submitOnlineOrder = async () => {
    if (!isOrderingMode) return;
    const tableLabel = String(menuTableLabel || "").trim();
    if (!tableLabel) {
      setOrderError("Merci de renseigner votre numéro de table.");
      return;
    }
    if (!cartItems.length) {
      setOrderError("Votre panier est vide.");
      return;
    }
    setSubmittingOrder(true);
    setOrderError("");
    try {
      const payload = {
        tenant_subdomain: tenantSubdomain || undefined,
        table_label: tableLabel,
        items: cartItems.map((item) => ({
          product_id: item.product_id,
          quantity: Number(item.quantity || 1),
          variation_id: item.variation_id || null,
          extras: Array.isArray(item.extras) ? item.extras : [],
        })),
        client_session_id: clientSessionIdRef.current || null,
        client_cache_token:
          activeTracking?.tracking_code ||
          `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      };
      const response = await fetch("/api/cloud/public/menu/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "Impossible d'envoyer la commande.");
      }
      const status = String(data?.status || "pending").toLowerCase();
      const nextTracking = {
        tracking_code: data?.tracking_code,
        tracking_internal_code: data?.tracking_internal_code || null,
        tracking_day_key: data?.tracking_day_key || null,
        public_order_id: data?.public_order_id || null,
        status,
        raw_status: status,
        table_label: tableLabel,
        order: data?.order || null,
      };
      setActiveTracking(nextTracking);
      setTrackingCodeInput(String(data?.tracking_code || "").toUpperCase());
      setCartItems([]);
      setShowCartDrawer(false);
    } catch (error) {
      setOrderError(error?.message || "Erreur d'envoi de commande.");
    } finally {
      setSubmittingOrder(false);
    }
  };

  const cancelPendingOrder = async () => {
    const trackingCode = activeTracking?.tracking_code;
    if (!trackingCode) return;
    try {
      const qs = tenantSubdomain ? `?subdomain=${encodeURIComponent(tenantSubdomain)}` : "";
      const response = await fetch(
        `/api/cloud/public/menu/orders/${encodeURIComponent(trackingCode)}/cancel${qs}`,
        { method: "POST" },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || "Annulation impossible");
      await fetchTracking(trackingCode, true);
    } catch (_e) {
      // ignore erreur UI bloquante
    }
  };

  useEffect(() => {
    if (!activeTracking?.tracking_code) return undefined;

    const terminalStatuses = new Set(["served", "rejected", "cancelled", "expired"]);
    if (terminalStatuses.has(String(activeTracking?.raw_status || activeTracking?.status || "").toLowerCase())) {
      return undefined;
    }

    if (activeTrackingPollRef.current) {
      clearInterval(activeTrackingPollRef.current);
    }
    activeTrackingPollRef.current = setInterval(() => {
      void fetchTracking(activeTracking.tracking_code, true);
    }, 5000);

    return () => {
      if (activeTrackingPollRef.current) {
        clearInterval(activeTrackingPollRef.current);
        activeTrackingPollRef.current = null;
      }
    };
  }, [activeTracking?.tracking_code, activeTracking?.raw_status, activeTracking?.status]);

  const clientStatusMeta = useMemo(() => {
    const status = String(activeTracking?.status || "pending").toLowerCase();
    if (status === "in_kitchen") return { label: "En cuisine", tone: "blue" };
    if (status === "ready") return { label: "Prêt", tone: "emerald" };
    if (status === "served") return { label: "Servi", tone: "emerald" };
    if (status === "rejected") return { label: "Refusée", tone: "rose" };
    if (status === "cancelled") return { label: "Annulée", tone: "slate" };
    if (status === "expired") return { label: "Expirée", tone: "slate" };
    return { label: "En attente", tone: "amber" };
  }, [activeTracking]);
  const clientStatusSteps = useMemo(() => {
    const status = String(activeTracking?.status || "pending").toLowerCase();
    const kitchenReached = ["in_kitchen", "ready", "served"].includes(status);
    const readyReached = ["ready", "served"].includes(status);
    const servedReached = status === "served";
    return [
      { key: "pending", label: "En attente", done: true },
      { key: "in_kitchen", label: "En cuisine", done: kitchenReached },
      { key: "ready", label: "Prêt", done: readyReached },
      { key: "served", label: "Servi", done: servedReached },
    ];
  }, [activeTracking]);
  const clientStatusTimelineSteps = useMemo(() => {
    const status = String(activeTracking?.status || "pending").toLowerCase();
    const indexMap = {
      pending: 0,
      in_kitchen: 1,
      ready: 2,
      served: 3,
    };
    const currentIndex = Number.isInteger(indexMap[status]) ? indexMap[status] : 0;
    return clientStatusSteps.map((step, index) => {
      const phase = index < currentIndex ? "done" : index === currentIndex ? "current" : "upcoming";
      if (["cancelled", "rejected", "expired"].includes(status)) {
        return { ...step, phase: step.key === "pending" ? "done" : "upcoming" };
      }
      return { ...step, phase };
    });
  }, [activeTracking?.status, clientStatusSteps]);

  const openTrackingByCode = async () => {
    const code = String(trackingCodeInput || "").trim().toUpperCase();
    if (!code) {
      setOrderError("Veuillez saisir un code de suivi.");
      return;
    }
    setOrderError("");
    const found = await fetchTracking(code, false);
    if (!found) {
      setOrderError("Aucune commande trouvée pour ce code aujourd’hui.");
    }
  };
  const covers = useMemo(() => {
    const raw = restaurantInfo?.menu_covers;
    const list = Array.isArray(raw) ? raw : [];
    return list.map(String).filter(Boolean).slice(0, 3);
  }, [restaurantInfo]);
  const heroDescriptions = useMemo(() => {
    const raw = restaurantInfo?.menu_descriptions;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((t) => String(t || "").trim()).filter(Boolean).slice(0, 4);
  }, [restaurantInfo]);

  useEffect(() => {
    if (covers.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setCoverIndex((idx) => (idx + 1) % covers.length);
    }, 6500);
    return () => window.clearInterval(id);
  }, [covers.length]);

  useEffect(() => {
    if (heroDescriptions.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setDescIndex((idx) => (idx + 1) % heroDescriptions.length);
    }, 5200);
    return () => window.clearInterval(id);
  }, [heroDescriptions.length]);

  return (
    <div className="relative min-h-screen bg-transparent text-slate-100">
      {/* HERO (fixed: background + content) */}
      <div
        className="fixed inset-0 z-0 overflow-hidden bg-gradient-to-b from-[#050913] via-[#0b1527] to-[#050913]"
        ref={heroRef}
      >
        {covers.length > 0 ? (
          covers.map((src, idx) => (
            <img
              key={`${src}-${idx}`}
              src={src}
              alt=""
              className={[
                "absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 will-change-transform",
                idx === coverIndex ? "opacity-80" : "opacity-0",
              ].join(" ")}
              style={{
              transform: `scale(${heroScale})`,
                animation: "mooHeroZoom 16s ease-in-out infinite",
                animationDelay: `${idx * -2}s`,
              }}
              loading={idx === 0 ? "eager" : "lazy"}
            />
          ))
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-[#050913] via-[#0f1c33] to-[#050913]" />
        )}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-[#050913]"
          style={{ opacity: Math.min(1, 0.85 + heroDim * 0.45) }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,184,0,0.16),rgba(0,0,0,0)_55%)]" />

        <div className="relative z-10 mx-auto flex min-h-[100vh] w-full max-w-6xl flex-col items-center justify-center px-4 py-16 text-center sm:min-h-[70vh] sm:py-20">
          <div
            className="mx-auto w-full max-w-xl rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl shadow-black/40 backdrop-blur-[5px] transition-opacity duration-300 sm:p-8"
            style={{
              opacity: heroFade,
            }}
          >
            <div className="mx-auto h-[120px] w-[120px] overflow-hidden rounded-full border border-white/15 bg-white/10">
              <img
                src={logoPath || "/placeholder-menu.jpg"}
                alt="Logo"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>

            <h1 className="mt-5 text-2xl font-semibold text-white sm:text-3xl">
              {title}
            </h1>

            <div className="mt-3 min-h-[22px] text-sm text-slate-200/90 sm:text-base">
              {heroDescriptions.length > 0 ? (
                <div
                  key={descIndex}
                  className="transition-opacity duration-500"
                >
                  {heroDescriptions[descIndex]}
                </div>
              ) : (
                <div className="text-slate-300/80">
                  Une expérience culinaire pensée avec soin.
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-center gap-3">
              {restaurantInfo?.instagram ? (
                <a
                  href={
                    String(restaurantInfo.instagram).includes("http")
                      ? restaurantInfo.instagram
                      : `https://instagram.com/${String(restaurantInfo.instagram).replace(/^@/, "")}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-amber-400/40 hover:bg-white/10"
                  title="Instagram"
                >
                  <Instagram size={18} />
                </a>
              ) : null}
              {restaurantInfo?.facebook ? (
                <a
                  href={
                    String(restaurantInfo.facebook).includes("http")
                      ? restaurantInfo.facebook
                      : `https://facebook.com/${restaurantInfo.facebook}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-amber-400/40 hover:bg-white/10"
                  title="Facebook"
                >
                  <Facebook size={18} />
                </a>
              ) : null}
              {restaurantInfo?.phone ? (
                <a
                  href={`https://wa.me/${sanitizePhoneForWaMe(restaurantInfo.phone)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-amber-400/40 hover:bg-white/10"
                  title="WhatsApp"
                >
                  <MessageCircle size={18} />
                </a>
              ) : null}
              {restaurantInfo?.tiktok ? (
                <a
                  href={
                    String(restaurantInfo.tiktok).includes("http")
                      ? restaurantInfo.tiktok
                      : `https://tiktok.com/@${String(restaurantInfo.tiktok).replace(/^@/, "")}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-amber-400/40 hover:bg-white/10"
                  title="TikTok"
                >
                  <Music2 size={18} />
                </a>
              ) : null}
            </div>
          </div>

          <div
            className="mt-12 flex flex-col items-center gap-3 text-xs text-slate-300/70 transition-opacity duration-300"
            style={{ opacity: heroFade }}
          >
            <span>Faites défiler pour découvrir le menu</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200/80 shadow-sm animate-bounce">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="M12 5v14" />
                <path d="m19 12-7 7-7-7" />
              </svg>
            </span>
          </div>
        </div>
      </div>

      {/* CONTENT + HEADER */}
      <div className="relative z-10">
        <div className="h-[100vh] sm:h-[70vh]" />
        <div className="relative border-t border-white/10">
          <div
            ref={headerSentinelRef}
            data-moo-menu-sentinel="1"
            className="h-px w-full"
            aria-hidden="true"
          />
          <div
            ref={headerRef}
            data-moo-menu-header="1"
            data-moo-menu-pinned={headerPinned ? "1" : "0"}
            className={[
              "w-full border-b border-white/10 backdrop-blur-[4px]",
              headerPinned ? "fixed left-0 right-0 top-0 z-50" : "relative z-30",
            ].join(" ")}
            style={{ backgroundColor: `rgba(5, 9, 19, ${heroDim * 0.64})` }}
          >
            <div className="mx-auto w-full max-w-6xl px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 overflow-hidden rounded-full border border-white/10 bg-white/5">
                      <img
                        src={logoPath || "/placeholder-menu.jpg"}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.35em] text-amber-300/80">
                        Menu
                      </div>
                      <div className="mt-0.5 max-w-[210px] truncate text-base font-semibold text-white sm:max-w-none sm:text-lg">
                        {title}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!menuDisabled && (
                <div
                  ref={categoriesRef}
                  className="mt-4 flex items-center gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {visibleCategoryKeys.map((key) => {
                    const cat = categoriesWithDaily.find((c) => String(c.id) === String(key));
                    const label = cat?.name || "Catégorie";
                    const active = String(activeKey) === String(key);
                    return (
                      <button
                        key={key}
                        onClick={() => scrollToCategory(key)}
                        data-cat={String(key)}
                        className={[
                          "shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-all",
                          active
                            ? "border-amber-400/40 bg-gradient-to-r from-amber-400/20 to-orange-500/20 text-white"
                            : "border-white/10 bg-white/5 text-slate-200 hover:border-white/20 hover:bg-white/10",
                        ].join(" ")}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {headerPinned && <div style={{ height: headerHeight }} aria-hidden="true" />}

          <div
            className="backdrop-blur-[4px]"
            style={{ backgroundColor: `rgba(5, 9, 19, ${heroDim * 0.64})` }}
          >
            <div className="mx-auto w-full max-w-6xl px-4 py-8">
              {loading ? (
                <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-slate-200">
                  Chargement du menu…
                </div>
              ) : menuDisabled ? (
                <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center text-slate-200">
                  <p className="text-lg font-semibold text-white">
                    Cet établissement n’a pas de menu disponible pour le moment.
                  </p>
                  <p className="mt-2 text-sm text-slate-300/80">
                    Revenez bientôt pour découvrir les plats.
                  </p>
                </div>
              ) : (
                <div className="space-y-10">
                  {orderError && (
                    <div className="rounded-2xl border border-rose-300/25 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
                      {orderError}
                    </div>
                  )}
              {visibleCategoryKeys.map((key) => {
                const cat = categoriesWithDaily.find((c) => String(c.id) === String(key));
                const catName = cat?.name || "Catégorie";
                const list = productsByCategoryKey.get(String(key)) || [];
                if (list.length === 0) return null;

                return (
                  <section
                    key={key}
                    data-key={String(key)}
                    ref={(el) => {
                      if (el) sectionsRef.current.set(String(key), el);
                    }}
                    className="scroll-mt-40"
                  >
                    <div className="mb-4 flex items-end justify-between gap-3">
                      <div>
                        <h2 className="text-xl font-semibold text-white">{catName}</h2>
                        <p className="mt-1 text-sm text-slate-300/75">
                          {list.length} produit{list.length > 1 ? "s" : ""}
                        </p>
                      </div>
                      {String(key) === "daily" && (
                        <div className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs text-amber-200">
                          Offre du jour
                        </div>
                      )}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {list.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => openProduct(product)}
                          className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 text-left transition-all hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/10"
                        >
                          <div className="relative h-44 w-full overflow-hidden">
                            <img
                              src={getProductImageSrc(product)}
                              alt={product.name}
                              className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#050913] via-transparent to-transparent" />
                            <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                              {product.daily_menu_active_today && (
                                <span className="rounded-full border border-amber-400/30 bg-amber-400/15 px-3 py-1 text-xs font-semibold text-amber-200">
                                  Menu du jour
                                </span>
                              )}
                              {product.product_type === "variable" && (
                                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-100">
                                  Variantes
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="p-5">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-lg font-semibold text-amber-200">
                                  {product.name}
                                </div>
                                {product.description ? (
                                  <div className="mt-1 line-clamp-2 text-sm text-slate-300/85">
                                    {product.description}
                                  </div>
                                ) : (
                                  <div className="mt-1 text-sm text-slate-400/80">
                                    Un classique de la maison.
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="mt-4 flex items-end justify-between gap-3">
                              {renderPrice(product)}
                              <div className="text-right text-xs text-slate-400">
                                {Number(product.active_accompaniments || 0) +
                                  Number(product.active_supplements || 0) >
                                0 ? (
                                  <span>
                                    Options disponibles
                                  </span>
                                ) : (
                                  <span>Détails</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>
                );
              })}

              {visibleCategoryKeys.length === 0 && (
                <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-slate-200">
                  Aucun produit disponible pour le moment.
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>

      </div>

      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-[5px]"
          onClick={closeProduct}
          role="presentation"
        >
          <div
            className="flex w-full max-w-2xl max-h-[85vh] flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#071326] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="relative shrink-0">
              <img
                src={getProductImageSrc(selectedProduct)}
                alt={selectedProduct.name}
                className="h-56 w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071326] via-transparent to-transparent" />
              <button
                onClick={closeProduct}
                className="absolute right-4 top-4 rounded-xl border border-white/15 bg-black/40 p-2 text-white hover:bg-black/60"
              >
                ✕
              </button>
            </div>

            <div className="min-h-0 overflow-y-auto p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-2xl font-semibold text-amber-200">
                      {selectedProduct.name}
                    </h3>
                    {selectedProduct.daily_menu_active_today && (
                      <span className="rounded-full border border-amber-400/30 bg-amber-400/15 px-3 py-1 text-xs font-semibold text-amber-200">
                        Menu du jour
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-slate-300/85">
                    {selectedProduct.description || "Une recette pensée pour donner envie."}
                  </p>
                </div>
                <div className="shrink-0 text-right">{renderPrice(selectedProduct)}</div>
              </div>

              {selectedProduct.product_type === "variable" &&
                Array.isArray(selectedProduct.variations) &&
                selectedProduct.variations.length > 0 && (
                  <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-sm font-semibold text-white">
                      Variantes {isOrderingMode ? "(choisir 1)" : ""}
                    </div>
                    <div className="mt-3 space-y-2">
                      {selectedProduct.variations.map((variation) => {
                        const effective = resolveVariationEffectivePrice(variation);
                        const base = Number(variation.price ?? 0);
                        const isPromo =
                          !!variation.daily_menu_active_today &&
                          effective > 0 &&
                          base > 0 &&
                          effective !== base;
                        const isActive = Number(selectedVariationId) === Number(variation.id);
                        return (
                          <button
                            type="button"
                            key={variation.id}
                            onClick={() => isOrderingMode && setSelectedVariationId(Number(variation.id))}
                            className={`w-full text-left flex items-start justify-between gap-3 rounded-xl border p-3 transition ${
                              isOrderingMode
                                ? isActive
                                  ? "border-amber-300/70 bg-amber-400/10"
                                  : "border-white/10 bg-[#050913]/30 hover:border-amber-300/40"
                                : "border-white/10 bg-[#050913]/30"
                            }`}
                          >
                            <div>
                              <div className="text-sm font-semibold text-slate-100">
                                {buildVariationLabel(variation)}
                              </div>
                              {isPromo && (
                                <div className="mt-1 text-xs text-amber-200/90">
                                  Prix du jour
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-white">
                                {formatMoney(effective)}
                              </div>
                              {isPromo && (
                                <div className="text-xs text-slate-400 line-through">
                                  {formatMoney(base)}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

              <div className="mt-6">
                {(() => {
                  const extras = extrasByProductId.get(selectedProduct.id) || [];
                  const { accompaniments, supplements } = splitExtras(extras);
                  const hasAny = accompaniments.length > 0 || supplements.length > 0;
                  if (!hasAny && !extrasLoading) return null;

                  const renderExtra = (extra) => {
                    const price = Number(extra?.price ?? 0) || 0;
                    const isDefault = !!(extra?.is_default || extra?.isDefault);
                    const maxQ = Number(extra?.max_quantity ?? extra?.maxQuantity ?? 1);
                    const extraId = Number(extra?.id);
                    const selectedQty = Number(selectedExtraQuantities?.[extraId] || 0);
                    const stepDown = () =>
                      setSelectedExtraQuantities((prev) => {
                        const next = { ...(prev || {}) };
                        const current = Number(next[extraId] || 0);
                        const updated = Math.max(0, current - 1);
                        if (updated <= 0) delete next[extraId];
                        else next[extraId] = updated;
                        return next;
                      });
                    const stepUp = () =>
                      setSelectedExtraQuantities((prev) => {
                        const next = { ...(prev || {}) };
                        const current = Number(next[extraId] || 0);
                        const updated = Math.min(Math.max(1, maxQ), current + 1);
                        next[extraId] = updated;
                        return next;
                      });
                    return (
                      <div
                        key={`${extra.type}-${extra.id}`}
                        className="flex items-start justify-between gap-3 rounded-xl border border-white/10 bg-[#050913]/30 p-3"
                      >
                        <div>
                          <div className="text-sm font-semibold text-slate-100">
                            {extra.name}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-300/80">
                            {isDefault && (
                              <span className="rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-emerald-200">
                                Inclus
                              </span>
                            )}
                            {maxQ > 1 && (
                              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
                                Max {maxQ}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm font-semibold text-white">
                          {extra.type === "accompaniment" || price <= 0
                            ? "Gratuit"
                            : formatMoney(price)}
                        </div>
                        {isOrderingMode && Number.isFinite(extraId) && (
                          <div className="ml-auto flex items-center gap-2">
                            <button
                              type="button"
                              onClick={stepDown}
                              className="h-8 w-8 rounded-full border border-white/15 bg-white/5 text-white flex items-center justify-center hover:bg-white/10"
                            >
                              <Minus size={14} />
                            </button>
                            <div className="w-8 text-center text-sm font-semibold text-white">
                              {selectedQty}
                            </div>
                            <button
                              type="button"
                              onClick={stepUp}
                              className="h-8 w-8 rounded-full border border-emerald-300/30 bg-emerald-400/10 text-emerald-100 flex items-center justify-center hover:bg-emerald-400/20"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  };

                  const block = (title, items) => {
                    if (items.length === 0) return null;
                    return (
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="text-sm font-semibold text-white">{title}</div>
                        <div className="mt-3 space-y-2">{items.map(renderExtra)}</div>
                      </div>
                    );
                  };

                  if (!extrasLoading && !hasAny) return null;

                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-white">
                          Accompagnements & suppléments
                        </div>
                        {extrasLoading && (
                          <div className="text-xs text-slate-400">Chargement…</div>
                        )}
                      </div>

                      {hasAny && (
                        <div className="mt-3 grid gap-4 sm:grid-cols-2">
                          {block("Accompagnements", accompaniments)}
                          {block("Suppléments", supplements)}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {selectionError && (
                <div className="mt-6 rounded-2xl border border-rose-300/25 bg-rose-400/10 p-3 text-sm text-rose-100">
                  {selectionError}
                </div>
              )}
              {isOrderingMode && (
                <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-300/70">
                      Quantité
                    </div>
                    <div className="text-sm font-semibold text-amber-200">
                      Total ligne: {formatMoney(selectedLineTotal)}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectionQuantity((prev) => Math.max(1, Number(prev || 1) - 1))
                      }
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white hover:bg-white/10"
                    >
                      <Minus size={16} />
                    </button>
                    <div className="min-w-[44px] text-center text-xl font-semibold text-white">
                      {Math.max(1, Number(selectionQuantity || 1))}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectionQuantity((prev) => Math.min(99, Number(prev || 1) + 1))
                      }
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-amber-300/35 bg-amber-400/15 text-amber-100 hover:bg-amber-400/20"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={handleAddSelectedProductToCart}
                      className="ml-auto rounded-2xl border border-emerald-300/30 bg-emerald-400/15 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/25"
                    >
                      Ajouter au panier
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isOrderingMode && (
        <>
          <button
            type="button"
            onClick={() => setShowCartDrawer(true)}
            className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-3 rounded-full border border-amber-300/40 bg-[#0b1527]/95 px-4 py-3 text-white shadow-2xl shadow-black/50 backdrop-blur hover:bg-[#12233f]"
          >
            <ShoppingBag size={18} className="text-amber-200" />
            <span className="text-sm font-semibold">Panier</span>
            <span className="rounded-full bg-amber-400 px-2 py-0.5 text-xs font-bold text-slate-900">
              {cartItemsCount}
            </span>
          </button>

          <div
            className={`fixed inset-0 z-40 transition ${
              showCartDrawer ? "pointer-events-auto" : "pointer-events-none"
            }`}
          >
            <div
              onClick={() => setShowCartDrawer(false)}
              className={`absolute inset-0 bg-black/50 backdrop-blur-[2px] transition-opacity ${
                showCartDrawer ? "opacity-100" : "opacity-0"
              }`}
            />
            <aside
              className={`absolute right-0 top-0 h-full w-full max-w-md border-l border-white/10 bg-[#050d1c]/95 shadow-2xl backdrop-blur transition-transform duration-300 ${
                showCartDrawer ? "translate-x-0" : "translate-x-full"
              }`}
            >
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-amber-300/90">
                      Panier
                    </div>
                    <div className="mt-1 text-lg font-semibold text-white">
                      {cartItemsCount} article{cartItemsCount > 1 ? "s" : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCartDrawer(false)}
                    className="rounded-xl border border-white/15 bg-white/5 p-2 text-white hover:bg-white/10"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                  {cartItems.length === 0 ? (
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300/80">
                      Votre panier est vide.
                    </div>
                  ) : (
                    cartItems.map((line) => {
                      const signature = computeLineSignature(line);
                      return (
                        <div
                          key={signature}
                          className="rounded-2xl border border-white/10 bg-white/5 p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-white">
                                {line.product_name}
                              </div>
                              {line.variation_attributes && (
                                <div className="mt-1 text-xs text-slate-300/70">
                                  {buildVariationLabel({
                                    attributes: line.variation_attributes,
                                    name: line.variation_name,
                                  })}
                                </div>
                              )}
                              <div className="mt-1 text-xs text-slate-300/80">
                                {formatMoney(line.unit_total_price)} x {line.quantity}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeCartLine(signature)}
                              className="rounded-lg border border-rose-300/25 bg-rose-400/10 p-2 text-rose-100 hover:bg-rose-400/20"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>

                          {Array.isArray(line.extras) && line.extras.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {line.extras.map((extra) => (
                                <div
                                  key={`${signature}-x-${extra.id}`}
                                  className="text-xs text-slate-300/75"
                                >
                                  {extra.quantity > 1 ? `${extra.quantity}x ` : ""}
                                  {extra.name}
                                  {Number(extra.price || 0) > 0
                                    ? ` (+${formatMoney(extra.price * extra.quantity)})`
                                    : ""}
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="mt-3 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => adjustCartLineQty(signature, -1)}
                              className="rounded-full border border-white/15 bg-white/5 p-1.5 text-white hover:bg-white/10"
                            >
                              <Minus size={14} />
                            </button>
                            <div className="w-8 text-center text-sm font-semibold text-white">
                              {line.quantity}
                            </div>
                            <button
                              type="button"
                              onClick={() => adjustCartLineQty(signature, +1)}
                              className="rounded-full border border-amber-300/30 bg-amber-400/10 p-1.5 text-amber-100 hover:bg-amber-400/20"
                            >
                              <Plus size={14} />
                            </button>
                            <div className="ml-auto text-sm font-semibold text-emerald-200">
                              {formatMoney((Number(line.unit_total_price || 0) || 0) * (Number(line.quantity || 0) || 0))}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="border-t border-white/10 px-4 py-4">
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-300/70">
                    Numéro de table
                  </label>
                  <input
                    type="text"
                    value={menuTableLabel}
                    onChange={(e) => setMenuTableLabel(e.target.value)}
                    placeholder="Ex: 12"
                    className="mt-2 w-full rounded-2xl border border-white/15 bg-[#020617]/60 px-4 py-3 text-white placeholder:text-slate-400 focus:border-amber-300/60 focus:outline-none"
                  />
                  <div className="mt-3 flex items-center justify-between text-sm text-slate-200">
                    <span>Total</span>
                    <span className="font-semibold text-amber-200">{formatMoney(cartTotal)}</span>
                  </div>
                  <button
                    type="button"
                    disabled={!canSubmitOrder}
                    onClick={submitOnlineOrder}
                    className={`mt-4 w-full rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                      canSubmitOrder
                        ? "border border-emerald-300/40 bg-emerald-400/15 text-emerald-100 hover:bg-emerald-400/25"
                        : "cursor-not-allowed border border-white/10 bg-white/5 text-slate-400"
                    }`}
                  >
                    {submittingOrder ? "Envoi..." : "Confirmer la commande"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTrackingModal(true)}
                    className="mt-3 w-full rounded-2xl border border-amber-300/30 bg-amber-400/10 px-4 py-3 text-sm font-semibold text-amber-100 transition hover:bg-amber-400/20"
                  >
                    Suivre ma commande
                  </button>
                  {activeTracking?.tracking_code && (
                    <p className="mt-2 text-center text-xs text-slate-300/75">
                      Code actif:{" "}
                      <span className="font-semibold text-amber-200">{activeTracking.tracking_code}</span>
                    </p>
                  )}
                </div>
              </div>
            </aside>
          </div>

          {showTrackingModal && (
            <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center">
              <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-white/15 bg-[#07132a] text-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-amber-300/90">
                      Suivi commande
                    </p>
                    <h3 className="mt-1 text-xl font-semibold text-white">Suivre ma commande</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTrackingModal(false)}
                    className="rounded-xl border border-white/15 bg-white/5 p-2 text-white hover:bg-white/10"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="space-y-4 p-5">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-slate-300/70">
                      Code de suivi
                    </label>
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={trackingCodeInput}
                        onChange={(e) => setTrackingCodeInput(e.target.value)}
                        placeholder="Ex: 001"
                        className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:border-amber-300/60 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={openTrackingByCode}
                        disabled={trackingLoading}
                        className="shrink-0 rounded-xl border border-amber-300/30 bg-amber-400/15 px-4 text-sm font-semibold text-amber-100 hover:bg-amber-400/25 disabled:opacity-60"
                      >
                        {trackingLoading ? "..." : "Voir"}
                      </button>
                    </div>
                  </div>

                  {activeTracking ? (
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-white">
                          Code {activeTracking.tracking_code}
                        </div>
                        <span
                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                            statusClassMap[clientStatusMeta.tone] || statusClassMap.slate
                          }`}
                        >
                          {clientStatusMeta.label}
                        </span>
                      </div>
                      <div className="mt-4 space-y-3">
                        {clientStatusTimelineSteps.map((step, index) => {
                          const isDone = step.phase === "done";
                          const isCurrent = step.phase === "current";
                          return (
                            <div key={step.key} className="flex items-start gap-3">
                              <div className="flex flex-col items-center">
                                <div
                                  className={`h-5 w-5 rounded-full border ${
                                    isDone
                                      ? "border-emerald-300/70 bg-emerald-400/30"
                                      : isCurrent
                                        ? "border-amber-300/70 bg-amber-400/30"
                                        : "border-white/20 bg-white/5"
                                  }`}
                                />
                                {index < clientStatusTimelineSteps.length - 1 && (
                                  <div
                                    className={`mt-1 h-6 w-px ${
                                      isDone ? "bg-emerald-300/60" : "bg-white/20"
                                    }`}
                                  />
                                )}
                              </div>
                              <div className="pb-1">
                                <p
                                  className={`text-sm font-medium ${
                                    isCurrent
                                      ? "text-amber-100"
                                      : isDone
                                        ? "text-emerald-100"
                                        : "text-slate-300/85"
                                  }`}
                                >
                                  {step.label}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {String(activeTracking?.raw_status || "").toLowerCase() === "pending" && (
                        <button
                          type="button"
                          onClick={cancelPendingOrder}
                          className="mt-4 rounded-xl border border-rose-300/30 bg-rose-400/10 px-3 py-2 text-xs font-semibold text-rose-100 hover:bg-rose-400/20"
                        >
                          Annuler la commande
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-300/80">
                      Saisissez votre code pour suivre l’évolution de la commande.
                    </div>
                  )}

                  {orderError && (
                    <div className="rounded-2xl border border-rose-300/25 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
                      {orderError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="border-t border-white/10 bg-[#050913]/70">
        <div className="mx-auto w-full max-w-6xl px-4 py-6 text-center text-xs text-slate-400">
          Powered by MOO Sync — {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
